import {MaterialSettingsTabs} from '@domain/materials'
import {Card} from '@hconnect/uikit/src/lib2'
import {useIsMutating} from '@tanstack/react-query'
import React, {useState, useCallback} from 'react'
import {FormProvider} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {HeaderTab, HeaderTabs} from '../../../../common/components/HeaderTabs'
import {getUrl, PLANT_ROUTES, useUrlParam} from '../../../../routing'
import {useAddMaterial, useMaterialsQuery} from '../../../hooks'
import {MaterialSettingsGeneralTab} from '../../materials-tabs'
import {MaterialDetailsCardHeader} from '../common/MaterialDetailsCardHeader'

import {useNewMaterialForm, useNewMaterialFormSubmit, useNewMaterialTabs} from './hooks'

export const NewMaterialDetailsCard: React.FC = () => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const navigate = useNavigate()

  const runningMutations = useIsMutating({mutationKey: ['editMaterial']})
  const isMaterialSaving = Boolean(runningMutations)

  const newMaterialForm = useNewMaterialForm()
  const availableTabs = useNewMaterialTabs(newMaterialForm)
  const [selectedTab, setSelectedTab] = useState<HeaderTab>(availableTabs[0])

  const {data: materials} = useMaterialsQuery()
  const {mutate: addMaterial} = useAddMaterial(materials)

  const handleSave = useNewMaterialFormSubmit(materials, newMaterialForm)

  const handleSaveAndNavigateToClassification = useCallback(() => {
    return newMaterialForm.handleSubmit((params) => {
      const {type, name, description, source} = params

      if (type === null || source === null || !newMaterialForm.formState.isDirty) {
        return
      }

      return addMaterial(
        {
          plantCode,
          dto: {type, name, description, source}
        },
        {
          onError: () => newMaterialForm.reset(),
          onSuccess: (createdMaterial) => {
            void navigate(
              `${getUrl(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.ID.MATERIAL_ID.path, {
                plantCode,
                materialType: createdMaterial.type,
                materialId: String(createdMaterial.id)
              })}?tab=classification`
            )
          }
        }
      )
    })()
  }, [newMaterialForm, plantCode, addMaterial, navigate])

  const handleTabChange = useCallback(
    async (tab: HeaderTab) => {
      if (tab.value === (MaterialSettingsTabs.Classification as string)) {
        if (newMaterialForm.formState.isDirty) {
          const isFormValid = await newMaterialForm.trigger()
          if (isFormValid) {
            await handleSaveAndNavigateToClassification()
          }
        }
        return
      }
      setSelectedTab(tab)
    },
    [handleSaveAndNavigateToClassification, newMaterialForm]
  )

  return (
    <Card
      headerContent={
        <MaterialDetailsCardHeader
          headerTitle={t('materialsSettings.newMaterial')}
          isCancelDisabled={isMaterialSaving}
          isSaveDisabled={isMaterialSaving || !newMaterialForm.formState.isDirty}
          onCancelClick={() => navigate(getUrl(PLANT_ROUTES.MATERIALS.path, {plantCode}))}
          onSaveClick={handleSave}
        />
      }
    >
      <HeaderTabs
        tabs={availableTabs}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        testIdPrefix={'material-settings-card'}
        sxTabs={{mb: 3}}
        sxTab={{color: ({palette}) => palette.common.black, fontWeight: 500}}
      />
      <FormProvider {...newMaterialForm}>
        <MaterialSettingsGeneralTab material={undefined} />
      </FormProvider>
    </Card>
  )
}
